import { useProfile } from "@shared/hooks/useProfile";
import theme from "@shared/utils/themeColors";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { SupportMessage } from "../types";
import SupportMessageItem from "./SupportMessageItem";

interface SupportMessageListProps {
  messages: SupportMessage[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => Promise<boolean>;
  onDeleteMessage?: (messageId: string) => Promise<void>;
  typingUsers?: Record<string, boolean>;
}

// Date separator component
const DateSeparator: React.FC<{ date: Date }> = ({ date }) => {
  const formattedDate = new Date(date).toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="flex items-center justify-center my-4">
      <div
        className={`px-4 py-1 rounded-full text-xs ${theme.text.secondary} bg-gray-100 dark:bg-gray-800`}
      >
        {formattedDate}
      </div>
    </div>
  );
};

const SupportMessageList: React.FC<SupportMessageListProps> = ({
  messages,
  loading,
  hasMore,
  onLoadMore,
  onDeleteMessage,
  typingUsers,
}) => {
  const { user } = useProfile();
  const containerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [newMessages, setNewMessages] = useState(0);
  const prevMessagesLengthRef = useRef(messages.length);
  const isInitialLoadRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const lastMessageTimestampRef = useRef<number | null>(null);

  // Reset flags when user changes
  useEffect(() => {
    isInitialLoadRef.current = true;
    lastMessageTimestampRef.current = null;
    setNewMessages(0);
  }, []);

  // Scroll to bottom utility function with delay option
  const scrollToBottom = useCallback((delay = 0) => {
    if (delay > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, delay);
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  // Handle initial load scroll
  useEffect(() => {
    if (loading || !messages.length || !isInitialLoadRef.current) return;

    // Initial load should scroll to bottom with a small delay for media
    scrollToBottom();
    scrollToBottom(500);
    isInitialLoadRef.current = false;

    // Store the timestamp of the most recent message
    const lastMessage = messages[messages.length - 1];
    lastMessageTimestampRef.current = lastMessage.timestamp?.getTime() || null;
  }, [loading, messages, scrollToBottom]);

  // Handle scroll position and load more
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = async () => {
      const { scrollTop, scrollHeight, clientHeight } = container;

      // Check if near bottom
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const shouldBeNearBottom = distanceFromBottom < 200;

      if (shouldBeNearBottom !== isNearBottom) {
        setIsNearBottom(shouldBeNearBottom);
        if (shouldBeNearBottom) {
          setNewMessages(0);
        }
      }

      // Load more when near top and not already loading
      if (scrollTop < 200 && !isLoadingMoreRef.current && hasMore && !loading) {
        isLoadingMoreRef.current = true;

        const previousHeight = container.scrollHeight;
        const previousScrollTop = container.scrollTop;

        const loaded = await onLoadMore();

        if (loaded) {
          // Keep scroll position after loading older messages
          requestAnimationFrame(() => {
            const newScrollHeight = container.scrollHeight;
            const heightDiff = newScrollHeight - previousHeight;
            container.scrollTop = previousScrollTop + heightDiff;
          });
        }

        isLoadingMoreRef.current = false;
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [hasMore, isNearBottom, loading, onLoadMore]);

  // Handle messages updates
  useEffect(() => {
    if (loading || isInitialLoadRef.current) return;

    const newMessageCount = messages.length - prevMessagesLengthRef.current;

    if (newMessageCount > 0 && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      const lastMessageTimestamp = lastMessage.timestamp?.getTime() || null;

      // Check if this is a new message (more recent than our last known message)
      const isNewMessage =
        lastMessageTimestampRef.current === null ||
        (lastMessageTimestamp !== null &&
          lastMessageTimestamp > lastMessageTimestampRef.current);

      if (isNewMessage) {
        const isOwnMessage = lastMessage.senderId === user?.id;

        // Scroll to bottom only for new messages when they're our own or we're near bottom
        if (isOwnMessage || isNearBottom) {
          const isMediaMessage =
            lastMessage.type === "image" || lastMessage.type === "video";
          scrollToBottom(isMediaMessage ? 500 : 100);
        } else {
          setNewMessages((prev) => prev + newMessageCount);
        }

        // Update the last known message timestamp
        lastMessageTimestampRef.current = lastMessageTimestamp;
      }
    }

    prevMessagesLengthRef.current = messages.length;
  }, [messages, user?.id, isNearBottom, loading, scrollToBottom]);

  // Hacer scroll automático cuando alguien está escribiendo
  useEffect(() => {
    const someoneTyping = Object.values(typingUsers || {}).some(
      (isTyping) => isTyping
    );

    // Si alguien está escribiendo y el usuario está cerca del final, hacer scroll al final
    if (someoneTyping && isNearBottom) {
      scrollToBottom();
    }
  }, [typingUsers, isNearBottom, scrollToBottom]);

  const shouldShowDateSeparator = (
    currentMsg: SupportMessage,
    prevMsg?: SupportMessage
  ) => {
    if (!prevMsg) return true;

    const currentDate = new Date(currentMsg.timestamp);
    const prevDate = new Date(prevMsg.timestamp);

    return (
      currentDate.getDate() !== prevDate.getDate() ||
      currentDate.getMonth() !== prevDate.getMonth() ||
      currentDate.getFullYear() !== prevDate.getFullYear()
    );
  };

  const renderMessages = () => {
    return messages
      .map((message, index) => {
        const elements = [];
        const prevMessage = messages[index - 1];

        if (shouldShowDateSeparator(message, prevMessage)) {
          elements.push(
            <DateSeparator
              key={`date-${message.id}`}
              date={message.timestamp}
            />
          );
        }

        elements.push(
          <SupportMessageItem
            key={message.id}
            message={message}
            onDelete={async (messageId) => {
              try {
                await onDeleteMessage?.(messageId);
              } catch (error) {
                console.error("Error deleting message:", error);
              }
            }}
          />
        );

        return elements;
      })
      .flat();
  };

  return (
    <div className="grow overflow-y-auto px-4" ref={containerRef}>
      {/* Loading indicator for more messages */}
      {loading && hasMore && (
        <div className="flex justify-center py-2">
          <div
            className={`animate-spin rounded-full h-5 w-5 border-b-2 ${theme.border.brand}`}
          ></div>
        </div>
      )}

      {/* Messages */}
      {messages.length === 0 && !loading ? (
        <div className="flex flex-col items-center justify-center h-full">
          <img
            src="/assets/images/avatar.png"
            alt="Bee Support"
            className="w-16 h-16 rounded-full mb-4"
          />
          <p className={`text-center ${theme.text.secondary}`}>
            Welcome to Support Chat! How can we help you today?
          </p>
        </div>
      ) : (
        renderMessages()
      )}

      {/* Typing indicator */}
      {Object.values(typingUsers || {}).some((isTyping) => isTyping) && (
        <div className="flex items-center my-2">
          <div className="flex-shrink-0 mr-3">
            <img
              src="/assets/images/avatar.png"
              alt="Bee Support"
              className="w-8 h-8 rounded-full"
            />
          </div>
          <div
            className={`p-2 rounded-lg rounded-bl-none ${theme.text.secondary} bg-gray-200 dark:bg-gray-800`}
          >
            <div className="flex space-x-1">
              <div
                className="w-2 h-2 rounded-full bg-gray-500 dark:bg-gray-400 animate-bounce"
                style={{ animationDelay: "0ms" }}
              ></div>
              <div
                className="w-2 h-2 rounded-full bg-gray-500 dark:bg-gray-400 animate-bounce"
                style={{ animationDelay: "150ms" }}
              ></div>
              <div
                className="w-2 h-2 rounded-full bg-gray-500 dark:bg-gray-400 animate-bounce"
                style={{ animationDelay: "300ms" }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* New message notification */}
      {newMessages > 0 && !isNearBottom && (
        <div
          className={`fixed bottom-20 left-1/2 transform -translate-x-1/2 ${theme.bg.brand} ${theme.text.black} px-4 py-2 rounded-full cursor-pointer shadow-md flex items-center z-10`}
          onClick={() => {
            scrollToBottom();
            setNewMessages(0);
          }}
        >
          <span>
            {newMessages} new {newMessages === 1 ? "message" : "messages"}
          </span>
          <svg className="w-4 h-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}

      {/* Invisible element to scroll to */}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default SupportMessageList;
